/** The client form of the forecast page's `restate-fpos`, for a grid with no cube. */

import { type CubeWindow } from "../../components/cubeDataTable/utils/columnUtils";
import { forecastRowId } from "../../components/cubeDataTable/utils/rowId";
import { type ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import { type MetricValues } from "../summary/editPropagation";
import {
  buildSlots,
  rollUpTemporalAggregates,
  type TemporalGridRow,
  type WeekSlot
} from "./temporalGridAdapter";
import {
  restateTargetStock,
  TARGET_STOCK_RESTATE_AUCS,
  TARGET_STOCK_RESTATE_FLOWS,
  TARGET_STOCK_RESTATE_TAIL
} from "./weeklyKpiEngine";

/** What to reset, and where. */
export interface TargetStockReset {
  /** The producer whose rows are reset. */
  producer: string;
  /** The FPOS, in weeks, that every open week gets. */
  fpos: number;
  /** The open weeks, by `year_week`; every other week keeps every value. */
  window: CubeWindow;
}

/** A reset's row transaction, and the target-stock cells whose value it changed. */
export interface TargetStockResetResult {
  /** The rows to apply as an AG Grid `update` transaction. */
  update: TemporalGridRow[];
  /** Each changed target-stock cell, keyed as the grid keys its rows. */
  changedCells: { rowId: string; colId: string }[];
}

function rowString(row: TemporalGridRow, key: string): string {
  const value = row[key];
  return typeof value === "string" ? value : "";
}

/** The first and last slot inside `window`; a slot the metadata does not map is outside. */
function openRange(
  yearWeeks: (number | undefined)[],
  window: CubeWindow
): { fromIndex: number; toIndex: number } | null {
  const open = yearWeeks.flatMap((week, index) =>
    week != null && week >= window.yearWeekMin && week <= window.yearWeekMax
      ? [index]
      : []
  );
  if (open.length === 0) return null;
  return { fromIndex: open[0], toIndex: open[open.length - 1] };
}

/**
 * The rows a reset rewrites: each flow whose sales and stock rows every
 * fiscal year carries, and the AUCs when both are. `null` without the units
 * flow.
 */
function writtenMetrics(
  baseRows: TemporalGridRow[],
  slots: WeekSlot[]
): { written: Set<string>; targetStock: Set<string> } | null {
  const metricsByYear = [...new Set(slots.map((slot) => slot.fiscalYear))].map(
    (year) =>
      new Set(
        baseRows
          .filter((row) => rowString(row, "fiscal_year_name") === year)
          .map((row) => rowString(row, "metric_name"))
      )
  );
  const flows = TARGET_STOCK_RESTATE_FLOWS.filter((flow) =>
    metricsByYear.every((metrics) =>
      flow.reads.every((metric) => metrics.has(metric))
    )
  );
  if (flows[0] !== TARGET_STOCK_RESTATE_FLOWS[0]) return null;
  return {
    written: new Set([
      ...flows.flatMap((flow) => flow.writes),
      ...(flows.length === TARGET_STOCK_RESTATE_FLOWS.length
        ? TARGET_STOCK_RESTATE_AUCS
        : [])
    ]),
    targetStock: new Set(flows.map((flow) => flow.writes[0]))
  };
}

/** The rows as the engine's weekly frame; a blank cell is NaN, as the server reads it. */
function weeksOf(
  baseRows: TemporalGridRow[],
  slots: WeekSlot[]
): MetricValues[] {
  return slots.map(function toWeek(slot) {
    const week: MetricValues = {};
    for (const row of baseRows) {
      if (rowString(row, "fiscal_year_name") !== slot.fiscalYear) continue;
      const value = row[slot.weekKey];
      week[rowString(row, "metric_name")] =
        typeof value === "number" ? value : Number.NaN;
    }
    return week;
  });
}

/**
 * Sets FPOS to `reset.fpos` in every open week of `rows` and restates target
 * stock and open-to-buy from it (`restateTargetStock`) — planned OTB through
 * the end of the frame — then rolls the fiscal years it touched up into their
 * period columns.
 *
 * A flow (units, at cost) is reset only when the rows carry its sales and
 * stock inputs; the AUCs only when both flows are reset. Returns `null` when
 * the units flow is missing or no week is open — nothing is reset then.
 */
export function resetTargetStockLocally(
  rows: TemporalGridRow[],
  reset: TargetStockReset,
  metaData: ForecastMetaDataType | undefined,
  forecastWeekFrom?: number
): TargetStockResetResult | null {
  const weekPeriodMapping = metaData?.weekPeriodMapping;
  if (!weekPeriodMapping) return null;
  const baseRows = rows.filter(
    (row) =>
      rowString(row, "producer_type") === reset.producer &&
      !rowString(row, "metric_name").startsWith("OPTION_")
  );
  const slots = buildSlots(baseRows, metaData.yearMapping);
  const yearWeeks = slots.map(
    (slot) => weekPeriodMapping[`${slot.fiscalYear}${slot.weekKey}`]?.yearWeek
  );
  const range = openRange(yearWeeks, reset.window);
  const metrics = writtenMetrics(baseRows, slots);
  if (!range || !metrics) return null;

  const weeks = weeksOf(baseRows, slots);
  const boundary = forecastWeekFrom
    ? yearWeeks.findIndex((week) => week != null && week >= forecastWeekFrom)
    : -1;
  // Planned OTB would go stale behind the restated OTB, and there is no server
  // to fall back on.
  if (forecastWeekFrom && boundary === -1) return null;
  const engine = restateTargetStock({
    weeks,
    fpos: reset.fpos,
    ...range,
    potbFromIndex: boundary === -1 ? undefined : boundary
  });
  // Planned OTB past the range only reaches years the metadata maps whole: the
  // roll-up refuses a year with an unmapped week.
  const tail = new Set<string>(TARGET_STOCK_RESTATE_TAIL);
  const tailYears = new Set(
    slots.slice(range.toIndex + 1).map((slot) => slot.fiscalYear)
  );
  const tailMapped = slots.every(
    (slot, index) => !tailYears.has(slot.fiscalYear) || yearWeeks[index] != null
  );
  const lastIndex =
    boundary === -1 || !tailMapped ? range.toIndex : slots.length - 1;
  // A flow whose inputs are missing would roll zero-filled sums into the
  // periods, so only the reset flows take the engine's values.
  const restated = weeks.map(function keepUnwritten(week, index) {
    const next = { ...week };
    if (index > lastIndex) return next;
    for (const metric of metrics.written) {
      const value = engine[index][metric];
      if (value !== undefined) next[metric] = value;
    }
    return next;
  });

  const updatedRows = baseRows.map(function clone(row) {
    return { ...row };
  });
  const rowByKey = new Map(
    updatedRows.map((row) => [
      `${rowString(row, "fiscal_year_name")}_${rowString(row, "metric_name")}`,
      row
    ])
  );
  const changedCells: TargetStockResetResult["changedCells"] = [];
  for (let index = range.fromIndex; index <= lastIndex; index++) {
    const slot = slots[index];
    for (const metric of metrics.written) {
      if (index > range.toIndex && !tail.has(metric)) continue;
      const row = rowByKey.get(`${slot.fiscalYear}_${metric}`);
      const value = restated[index][metric];
      if (!row || value === undefined) continue;
      // A blank cell arrives without its key, so it compares as null.
      const next = Number.isFinite(value) ? value : null;
      if (
        metrics.targetStock.has(metric) &&
        (row[slot.weekKey] ?? null) !== next
      ) {
        changedCells.push({ rowId: forecastRowId(row), colId: slot.weekKey });
      }
      row[slot.weekKey] = next;
    }
  }

  // The rows carry weekly cells alone and an update replaces a row whole, so
  // an untouched year's row stays out of the update or loses its periods.
  const touched = new Set(
    slots.slice(range.fromIndex, lastIndex + 1).map((slot) => slot.fiscalYear)
  );
  const rolled = slots.flatMap((slot, index) =>
    touched.has(slot.fiscalYear) ? [index] : []
  );
  // The roll-up throws on a week the metadata does not map: a month summed
  // over fewer weeks would be wrong, so nothing is reset.
  if (rolled.some((index) => yearWeeks[index] == null)) return null;
  rollUpTemporalAggregates(
    rolled.map((index) => slots[index]),
    rolled.map((index) => restated[index]),
    rowByKey,
    metaData,
    { deriveMetrics: true, addTotal: true }
  );
  return {
    update: updatedRows.filter((row) =>
      touched.has(rowString(row, "fiscal_year_name"))
    ),
    changedCells
  };
}
